// Packages
import React, { useEffect, useRef, useContext } from "react"
import PropTypes from "prop-types"
import Slider from "react-slick"
import getValue from "object-getvalue"
import { StackerContext } from "components/DYStackers/StackerContext"

// Components
import Swatch from "components/Stacker/components/Swatch/Swatch"

// Actions
import { selectSwatch } from "components/Stacker/actions/SwatchActions"

// Styles
import "./SwatchSet.scss"
import { refineSwatch } from "../../actions/StackerActions"

import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"

function SwatchSet({
  baseProductCode,
  defaultSelectedIndex,
  entryNumber,
  isEditCart,
  isPDP,
  productVariants,
  refinerId,
  showTooltip,
  swatchSettings,
  isSlider = true,
}) {
  const slider = useRef()
  const dispatch = useDispatch()
  const stackerContext = useContext(StackerContext)

  const selectedVariantIndex = useSelector((state) => {
    // if PDP then override the selectedVariantIndex
    if (isPDP) {
      return defaultSelectedIndex
    }
    if (refinerId) {
      return getValue(
        state,
        `stacker.refiners.${refinerId}.selectedVariantIndex`
      )
    }
    return getValue(
      state,
      `swatch.${baseProductCode}.selectedVariantIndex`,
      defaultSelectedIndex
    )
  })

  const handleSwatchChange = (index, variantCode) => {
    if (slider.current) {
      slider.current.slickGoTo(index, true)
    }
    const swatchData = {
      baseProductCode,
      selectedVariantIndex: Number(index),
      selectedVariantCode: variantCode,
      isEditCart,
      entryNumber,
    }
    if (refinerId) {
      const selInfo = {
        selectedVariant: productVariants[Number(index)],
        selectedVariantIndex: Number(index),
      }
      const R = {
        refinerId,
        selInfo,
        variantCode,
      }
      dispatch(refineSwatch({ data: R, retailerId: stackerContext.retailerId }))
    } else {
      dispatch(selectSwatch(swatchData))
    }
  }

  useEffect(() => {
    if (productVariants[defaultSelectedIndex]) {
      handleSwatchChange(
        defaultSelectedIndex,
        productVariants[defaultSelectedIndex].code
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedIndex, productVariants])

  return (
    <div className="SwatchSet">
      {/* <p>Swatches</p> */}
      {isSlider && (
        <form>
          <fieldset>
            <legend className="sr-only">Swatch Buttons</legend>
            <Slider {...swatchSettings} ref={slider}>
              {productVariants.map((productVariant, index) => (
                <Swatch
                  isChecked={index === selectedVariantIndex}
                  index={index}
                  color={productVariant.color}
                  colorImage={productVariant.colorImage}
                  variantCode={productVariant.code}
                  baseCode={baseProductCode}
                  handleSwatchChange={handleSwatchChange}
                  key={`${baseProductCode}-${productVariant.code}-${index}`} // eslint-disable-line react/no-array-index-key
                  tabindex={index}
                />
              ))}
            </Slider>
          </fieldset>
        </form>
      )}
      {!isSlider && (
        <form>
          <fieldset>
            <legend className="sr-only">Swatch Input</legend>
            {productVariants.map((productVariant, index) => (
              <Swatch
                isChecked={index === selectedVariantIndex}
                index={index}
                color={productVariant.color}
                colorImage={productVariant.colorImage}
                variantCode={productVariant.code}
                baseCode={baseProductCode}
                handleSwatchChange={handleSwatchChange}
                key={`${baseProductCode}-${productVariant.code}-${index}`} // eslint-disable-line react/no-array-index-key
                tabindex={index}
                showTooltip={showTooltip}
              />
            ))}
          </fieldset>
        </form>
      )}
    </div>
  )
}

SwatchSet.propTypes = {
  baseProductCode: PropTypes.string.isRequired,
  defaultSelectedIndex: PropTypes.number.isRequired,
  entryNumber: PropTypes.string,
  isEditCart: PropTypes.bool,
  isPDP: PropTypes.bool,
  isSlider: PropTypes.bool,
  productVariants: PropTypes.array.isRequired,
  refinerId: PropTypes.string,
  showTooltip: PropTypes.bool,
  swatchSettings: PropTypes.object.isRequired,
}

export default SwatchSet
